import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { Pagination } from '@core/models/pagination.model';
import { AdminManagementService } from '@core/services/admin-management.service';
import { BreadCrumbService } from '@core/services/bread-crumb.service';
import { NotificationFacade } from '@core/facades/notification.facade';
import { BaseSelectDirective } from '@shared/directives/base-select.directive';
import { ButtonDirective } from '@shared/directives/button.directive';
import { InputDateDirective } from '@shared/directives/input-date.directive';
import {
  DropDownModel,
  DropdownComponent,
} from '@shared/ui/dropdown/dropdown.component';
import { InputLabelComponent } from '@shared/ui/input-label/input-label.component';
import {
  BehaviorSubject,
  distinctUntilChanged,
  map,
  Observable,
  Subscription,
  take,
} from 'rxjs';
import { FILTER, IFILTER } from 'app/app.constants';
import { AccountManagementService } from '@core/services/account-management.service';
import { TableNavigatorComponent } from '@shared/ui/table-navigator/table-navigator.component';
import { ViewAdminListDatum } from '@core/models/admin/admin-management';
import { ConfirmDialogComponent } from '@shared/ui/confirm-dialog/confirm-dialog.component';
import { RolesAndPermissionsService } from '@core/services/roles-and-permissions.service';
import { AllRolesDatum } from '@core/models/admin/roles-and-permissions/all-roles.model';
import { dropdownMenuAnimation } from '@shared/animations/animations';
import { SearchPipe } from '@shared/pipes/search.pipe';
import { FiltersComponent } from '@shared/ui/filters/filters.component';
import { CdkPortal } from '@angular/cdk/portal';
import { Overlay, OverlayModule } from '@angular/cdk/overlay';
import { Store } from '@ngxs/store';
import { TableSelectors } from 'app/auth/table-store/table.selectors';
import { TableActions } from 'app/auth/table-store/table.actions';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { PermissionDirective } from '@shared/directives/permission.directive';
import { TableRefreshComponent } from 'app/ui/table-refresh/table-refresh.component';
interface ViewAdminListRole extends ViewAdminListDatum {
  role_name?: string;
}
@Component({
  selector: 'app-admin-users',
  standalone: true,
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './admin-users.component.html',
  styleUrl: './admin-users.component.scss',
  animations: [dropdownMenuAnimation],
  imports: [
    RouterOutlet,
    CommonModule,
    DropdownComponent,
    DropdownComponent,
    RouterLink,
    ButtonDirective,
    ReactiveFormsModule,
    FormsModule,
    SearchPipe,
    FiltersComponent,
    TableNavigatorComponent,
    OverlayModule,
    PermissionDirective,
    TableRefreshComponent
  ],
})
export class AdminUsersComponent implements OnInit, OnDestroy {
  filterMenuState: 'visible' | 'hidden' = 'hidden';
  showDropdown = false;
  subs: Subscription[] = [];
  usersList: ViewAdminListRole[] = [];
  totalCount: number = 0;
  actionList: DropDownModel[] = [];
  adminName!: string | null;
  adminId!: string | null;
  confirmQuestion: string = `Are you sure you want to deactivate this admin? This action cannot be undone.`;
  isDeleteDialogOpen: boolean = false;
  isDeleteInProgress: boolean = false;
  resendQuestion: string =
    'Are you sure you want to resend this admin an invitation?';
  isResendDialogOpen: boolean = false;
  isResendInProgress: boolean = false;
  isDeactivateDialogOpen: boolean = false;
  isDeactivateInProgress: boolean = false;
  isLoading: boolean = false;
  $index: any;
  searchText: any;
  usersListSubject = new BehaviorSubject<ViewAdminListRole[]>([]);
  usersList$ = this.usersListSubject.asObservable();
  totalCountSubject = new BehaviorSubject<number>(0);
  totalCount$ = this.totalCountSubject.asObservable();
  rolesSubject = new BehaviorSubject<AllRolesDatum[]>([]);
  roles$ = this.rolesSubject.asObservable();
  hadFilterBefore = false;
  isFilterActive: boolean = false;

  // @ViewChild('confirmDialog', { read: ViewContainerRef, static: true })
  // confirmDialog!: ViewContainerRef;


  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  notify = inject(NotificationFacade);
  breadCrumbService = inject(BreadCrumbService);
  adminService = inject(AdminManagementService);
  change = inject(ChangeDetectorRef);
  roleService = inject(RolesAndPermissionsService);
  searchService = inject(AccountManagementService);
  overlay = inject(Overlay);
  store = inject(Store);
  confirmDialogService = inject(ConfirmDialogService);

  isOpen = false;

  roles: AllRolesDatum[] = [];
  filterStructure: IFILTER[] = [
    {
      filterKey: 'role',
      label: 'Role',
      type: 'dropdown',
      dropdown: [],
    },
    {
      filterKey: 'status',
      label: 'Status',
      type: 'dropdown',
      dropdown: [
        { key: 'activated', value: 'Activated' },
        { key: 'de_activated', value: 'Deactivated' },
        { key: 'pending', value: 'Pending' },
      ],
    },
  ];

  constructor() {
    this.usersList$ = this.store.select(
      TableSelectors.getTableData('systemUsers')
    );
    this.totalCount$ = this.store.select(
      TableSelectors.getTableTotalCount('systemUsers')
    );
    this.roles$ = this.store.select(TableSelectors.getSystemUsersRoles());
  }

  ngOnInit() {
    this.breadCrumbService.setPageBreadCrumb(this.activatedRoute);

    this.loadAdminList();
    this.getRoles();
    this.listFiltersEventListern();
    // this.confirmDialogService.setViewContainerRef(confirmDialog);
  }

  loadAdminList(page = 1) {
    const sub = this.usersList$.pipe(take(1)).subscribe((users) => {
      const hasData = Array.isArray(users) && users.length > 0;
      // only dispatch if store is empty
      if (!hasData) {
        this.store.dispatch(
          new TableActions.LoadTableData({
            tableKey: 'systemUsers',
            params: new Pagination({
              skip: (page - 1) * 10,
              size: 10,
            }),
          })
        );
      }
    });
    this.subs.push(sub);
  }

  setFilterRoles() {
    const sub = this.roles$.subscribe((roles) => {
      this.filterStructure[0].dropdown = roles.map((role) => ({
        key: role.id,
        value: role.name,
      }));
      this.change.detectChanges();
    });
    this.subs.push(sub);
  }

  listFiltersEventListern() {
    this.activatedRoute.queryParams.subscribe((params) => {
      let query = '';
      if (params[this.filterStructure[0].filterKey]) {
        query = `role_id=${params['role']}&`;
      }
      if (params[this.filterStructure[1].filterKey]) {
        query += `status=${params['status']}&`;
      }
      if (params['search']) {
        query += `search_text=${params['search']}`;
      }

      const rawPage = params['page'] ? parseInt(params['page']) : 1;
      const page = rawPage > 1 ? rawPage - 1 : 0;
      const take = 10;
      const skip = page * take;

      if (query) {
        this.isFilterActive = true;
        this.hadFilterBefore = true; // filters are currently active
        this.store.dispatch(
          new TableActions.FilterSystemUsers({ filter: query })
        );
      } else {
        this.isFilterActive = false;
        // only re-fetch if there was a previous filter
        if (this.hadFilterBefore || params['page']) {
          this.hadFilterBefore = false;
          this.store.dispatch(
            new TableActions.LoadTableData({
              tableKey: 'systemUsers',
              params: new Pagination({ skip: skip, size: take }),
            })
          );
        }
      }
    });
  }

  getRoles() {
    const sub = this.roles$.subscribe((roles) => {
      if (!roles || roles.length === 0) {
        this.store.dispatch(new TableActions.GetSystemUsersRoles());
      }
      this.filterStructure[0].dropdown = roles.map((role) => ({
        key: role.id,
        value: role.name,
      }));
      this.change.detectChanges();
    });
    this.subs.push(sub);
  }

  toggle() {
    this.change.detectChanges();
  }

  fullyRegistered$(): Observable<number> {
    return this.usersList$.pipe(
      map(
        (users) => users?.filter((u) => u.status === 'activated').length || 0
      ),
      distinctUntilChanged()
    );
  }

  initDropdownList(adminUser: ViewAdminListDatum) {
    return (this.actionList = [
      {
        action: () => {
          this.viewUserDetails(adminUser);
        },
        title: 'View Details',
        show: true && this.roleService.hasPermission(['update_admin_profile_data']) && this.roleService.hasPermission(['delete_admin']),
      },
      {
        action: () => {
          this.editUserDetails(adminUser);
        },
        title: 'Edit',
        show: true && this.roleService.hasPermission(['update_admin_profile_data']),
      },
      {
        action: () => {
          this.adminId = adminUser.id;
          this.adminName = `${adminUser.first_name} ${adminUser.last_name}`;
          this.confirmDialogService.openDialog({
            isOpen: true,
            isDialogLoading: false,
            title: 'Yes, Delete Admin',
            description: `Are you sure you want to delete this admin: ${this.adminName}? This action cannot be undone.`,
            loadingText: 'Deleting Admin...',
            action: () => this.deleteAdmin(),
            onClose: () => {
              this.adminId = null;
              this.adminName = null;
            },
          });
        },
        title: 'Delete',
        show: true && this.roleService.hasPermission(['delete_admin']),
      },
      {
        action: () => {
          this.adminId = adminUser.id;
          this.adminName = `${adminUser.first_name} ${adminUser.last_name}`;
          this.confirmDialogService.openDialog({
            isOpen: true,
            isDialogLoading: false,
            title: 'Yes, Resend Invite',
            description: `Are you sure you want to resend the activation email to this admin: ${this.adminName}?`,
            loadingText: 'Resending invitation...',
            action: () => this.resendInvite(),
            onClose: () => {
              this.adminId = null;
              this.adminName = null;
            },
          });
        },
        title: 'Resend Invite',
        show: adminUser.status === 'pending' && this.roleService.hasPermission(['resend_admin_invite']),
      },
      {
        action: () => {
          this.adminId = adminUser.id;
          this.adminName = `${adminUser.first_name} ${adminUser.last_name}`;
          this.confirmDialogService.openDialog({
            isOpen: true,
            isDialogLoading: false,
            title: 'Yes, Deactivate Admin',
            description: `Are you sure you want to deactivate this admin: ${this.adminName}?`,
            loadingText: 'Deactivating Admin...',
            action: () => this.deactivateAdmin(),
            onClose: () => {
              this.adminId = null;
              this.adminName = null;
            },
          });
        },
        title: 'Deactivate',
        show: adminUser.status === 'activated' && this.roleService.hasPermission(['manage_admin_status']),
      },
      {
        action: () => {
          this.adminId = adminUser.id;
          this.adminName = `${adminUser.first_name} ${adminUser.last_name}`;
          this.confirmDialogService.openDialog({
            isOpen: true,
            isDialogLoading: false,
            title: 'Yes, Activate Admin',
            description: `Are you sure you want to activate this admin: ${this.adminName}?`,
            loadingText: 'Activating Admin...',
            action: () => this.activateAdmin(),
            onClose: () => {
              this.adminId = null;
              this.adminName = null;
            },
          });
        },
        title: 'Activate',
        show: adminUser.status === 'de_activated' && this.roleService.hasPermission(['manage_admin_status']),
      },
    ]);
  }

  viewUserDetails(user: ViewAdminListDatum) {
    this.router.navigate([`profile`, user.id], {
      relativeTo: this.activatedRoute,
      state: { user },
    });
  }

  editUserDetails(user: ViewAdminListDatum) {
    this.router.navigate(['edit', user.id], {
      relativeTo: this.activatedRoute,
      state: { user },
    });
  }

  deleteAdmin() {
    this.confirmDialogService.updateDialog({ isDialogLoading: true });
    if (!this.adminId) return;
    const sub = this.adminService.deleteAdmin(this.adminId).subscribe({
      next: () => {
        this.confirmDialogService.updateDialog({
          isDialogLoading: false,
          isOpen: false,
        });
        // this.getAdminList();
        this.usersList = this.usersList.filter(
          (adminUser) => adminUser.id !== this.adminId
        );
        this.change.detectChanges();

        this.notify.success('Admin deactivated successfully');
      },
      error: (err) => {
        this.confirmDialogService.updateDialog({ isDialogLoading: false });
        this.change.detectChanges();
      },
    });

    this.subs.push(sub);
  }

  resendInvite() {
    this.confirmDialogService.updateDialog({ isDialogLoading: true });
    this.change.detectChanges();
    if (!this.adminId) return;
    const sub = this.adminService.resendInvite(this.adminId).subscribe({
      next: () => {
        this.confirmDialogService.updateDialog({
          isDialogLoading: false,
          isOpen: false,
        });
        this.change.detectChanges();
        this.notify.success('Admin invite resent successfully');
      },
      error: (err) => {
        this.isResendInProgress = false;
        this.confirmDialogService.updateDialog({ isDialogLoading: false });
        this.change.detectChanges();
      },
    });

    this.subs.push(sub);
  }
  deactivateAdmin() {
    this.confirmDialogService.updateDialog({
      isDialogLoading: this.isResendInProgress,
    });
    if (!this.adminId) return;
    const sub = this.adminService.changeAdminStatus(this.adminId).subscribe({
      next: () => {
        this.confirmDialogService.updateDialog({
          isDialogLoading: false,
          isOpen: false,
        });
        this.store.dispatch(
          new TableActions.LoadTableData({
            tableKey: 'systemUsers',
            params: new Pagination({
              skip: 0,
              size: 10,
            }),
          })
        );
        this.change.detectChanges();

        this.notify.success('Admin deactivated successfully');
      },
      error: (err) => {
        this.confirmDialogService.updateDialog({ isDialogLoading: false });
        this.change.detectChanges();
      },
    });

    this.subs.push(sub);
  }

  activateAdmin() {
    this.confirmDialogService.updateDialog({ isDialogLoading: true });
    if (!this.adminId) return;
    const sub = this.adminService.changeAdminStatus(this.adminId).subscribe({
      next: () => {
        this.confirmDialogService.updateDialog({
          isDialogLoading: false,
          isOpen: false,
        });
        this.store.dispatch(
          new TableActions.LoadTableData({
            tableKey: 'systemUsers',
            params: new Pagination({
              skip: 0,
              size: 10,
            }),
          })
        );
        this.change.detectChanges();
        this.notify.success('Admin activated successfully');
      },
      error: () => {
        this.confirmDialogService.updateDialog({ isDialogLoading: false });
        this.change.detectChanges();
      },
    });
    this.subs.push(sub);
  }

  navigate(currentPage: number) {
    if (this.isFilterActive) {
      this.router.navigate([], {
        queryParams: { page: currentPage },

        queryParamsHandling: 'merge',
      });
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
